import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatIDR } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ShoppingCart, PackagePlus, Trash2, UtensilsCrossed, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export default function Food() {
  const [items, setItems] = useState([]);
  const [itemModal, setItemModal] = useState({ open: false, editing: null });
  const [saleModal, setSaleModal] = useState({ open: false, item: null });
  const [restockModal, setRestockModal] = useState({ open: false, item: null });

  const load = async () => {
    const data = await api.listFoods();
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus item "${item.name}"?`)) return;
    try {
      await api.deleteFood(item.id);
      toast.success('Item dihapus');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const stockBadge = (stock) => {
    if (stock <= 0) return 'text-red-400 bg-red-500/10 border-red-500/40';
    if (stock < 5) return 'text-amber-400 bg-amber-500/10 border-amber-500/40';
    return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40';
  };

  return (
    <div className="space-y-6" data-testid="food-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Makanan & Minuman</h2>
          <p className="text-slate-400 text-sm mt-1">Kelola stok, catat penjualan, dan pantau item dengan stok menipis.</p>
        </div>
        <Button
          data-testid="btn-add-food-item"
          onClick={() => setItemModal({ open: true, editing: null })}
          className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold gap-2 shadow-[0_0_15px_rgba(255,0,127,0.3)]"
        >
          <Plus className="w-4 h-4" /> Tambah Item
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-700 p-8 text-center text-slate-500">
            Belum ada item. Klik &quot;Tambah Item&quot; untuk memulai.
          </div>
        )}
        {items.map(item => (
          <div key={item.id} data-testid={`food-card-${item.id}`} className="rounded-xl bg-[#131B2E] p-5 neon-magenta-card">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 text-slate-400" />
                <span className="text-[10px] font-mono-tactical uppercase tracking-widest text-slate-400">{item.category}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={`btn-edit-food-${item.id}`}
                  onClick={() => setItemModal({ open: true, editing: item })}
                  className="p-1.5 rounded text-slate-500 hover:text-fuchsia-400 hover:bg-slate-800"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid={`btn-delete-food-${item.id}`}
                  onClick={() => handleDelete(item)}
                  className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-slate-800"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <h4 className="font-display font-semibold text-base text-slate-100 leading-tight">{item.name}</h4>
            <p className="text-cyan-400 font-mono-tactical text-lg font-bold mt-1">{formatIDR(item.price)}</p>

            <div className="mt-3 flex items-center justify-between">
              <span className={`text-xs font-mono-tactical uppercase tracking-widest px-2 py-1 rounded border ${stockBadge(item.stock)}`} data-testid={`stock-badge-${item.id}`}>
                Stok: {item.stock}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                data-testid={`btn-sell-${item.id}`}
                onClick={() => setSaleModal({ open: true, item })}
                disabled={item.stock <= 0}
                className="bg-emerald-500 hover:bg-emerald-400 text-[#090D16] font-semibold gap-1.5 disabled:opacity-40"
                size="sm"
              >
                <ShoppingCart className="w-3.5 h-3.5" /> Jual
              </Button>
              <Button
                data-testid={`btn-restock-${item.id}`}
                onClick={() => setRestockModal({ open: true, item })}
                variant="outline"
                className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-cyan-300 gap-1.5"
                size="sm"
              >
                <PackagePlus className="w-3.5 h-3.5" /> Restok
              </Button>
            </div>
          </div>
        ))}
      </div>

      <FoodFormDialog state={itemModal} onClose={() => setItemModal({ open: false, editing: null })} onSaved={load} />
      <SaleDialog state={saleModal} onClose={() => setSaleModal({ open: false, item: null })} onSaved={load} />
      <RestockDialog state={restockModal} onClose={() => setRestockModal({ open: false, item: null })} onSaved={load} />
    </div>
  );
}

function FoodFormDialog({ state, onClose, onSaved }) {
  const editing = state.editing;
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Makanan');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');

  useEffect(() => {
    if (state.open) {
      setName(editing?.name || '');
      setCategory(editing?.category || 'Makanan');
      setPrice(editing?.price?.toString() || '');
      setStock(editing?.stock?.toString() || '');
    }
  }, [state.open, editing]);

  const submit = async () => {
    if (!name.trim() || !price) {
      toast.error('Nama dan harga wajib diisi');
      return;
    }
    try {
      if (editing) {
        await api.updateFood(editing.id, { name: name.trim(), category, price: Number(price) });
        toast.success('Item diperbarui');
      } else {
        await api.createFood({ name: name.trim(), category, price: Number(price), stock: Number(stock || 0) });
        toast.success('Item ditambahkan');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-fuchsia-500/30 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? 'Edit Item' : 'Tambah Item Makanan/Minuman'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Nama Item</Label>
            <Input data-testid="input-food-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="misal: Indomie Goreng" className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Kategori</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="select-food-category" className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131B2E] border-slate-700 text-slate-100">
                <SelectItem value="Makanan">Makanan</SelectItem>
                <SelectItem value="Minuman">Minuman</SelectItem>
                <SelectItem value="Snack">Snack</SelectItem>
                <SelectItem value="Lainnya">Lainnya</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Harga (Rp)</Label>
              <Input data-testid="input-food-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="10000" className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical" />
            </div>
            {!editing && (
              <div>
                <Label className="text-slate-300">Stok Awal</Label>
                <Input data-testid="input-food-stock" type="number" value={stock} onChange={(e) => setStock(e.target.value)} placeholder="20" className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical" />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Batal</Button>
          <Button data-testid="btn-save-food" onClick={submit} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaleDialog({ state, onClose, onSaved }) {
  const item = state.item;
  const [qty, setQty] = useState('1');
  useEffect(() => { if (state.open) setQty('1'); }, [state.open]);

  const total = item ? Number(qty || 0) * item.price : 0;

  const submit = async () => {
    if (!item) return;
    try {
      await api.createSale({ item_id: item.id, qty: Number(qty) });
      toast.success(`Terjual · ${formatIDR(total)}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mencatat penjualan');
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-emerald-500/40 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">Catat Penjualan</DialogTitle>
        </DialogHeader>
        {item && (
          <>
            <div className="rounded-lg bg-[#0E1424] border border-slate-800 p-3 mb-2">
              <p className="font-semibold text-slate-100">{item.name}</p>
              <p className="text-xs text-slate-400">Stok: <span className="text-emerald-400 font-mono-tactical">{item.stock}</span> · Harga: <span className="text-cyan-400 font-mono-tactical">{formatIDR(item.price)}</span></p>
            </div>
            <div>
              <Label className="text-slate-300">Kuantitas</Label>
              <Input data-testid="input-food-qty" type="number" min="1" max={item.stock} value={qty} onChange={(e) => setQty(e.target.value)} className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical" />
            </div>
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-3 mt-2">
              <p className="text-xs uppercase tracking-widest font-mono-tactical text-slate-400">Total</p>
              <p className="text-2xl font-bold text-emerald-400 font-mono-tactical" data-testid="text-sale-total">{formatIDR(total)}</p>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Batal</Button>
          <Button data-testid="btn-confirm-sale" onClick={submit} className="bg-emerald-500 hover:bg-emerald-400 text-[#090D16] font-semibold gap-2">
            <ShoppingCart className="w-4 h-4" /> Konfirmasi Jual
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestockDialog({ state, onClose, onSaved }) {
  const item = state.item;
  const [qty, setQty] = useState('');
  useEffect(() => { if (state.open) setQty(''); }, [state.open]);

  const submit = async () => {
    if (!item || !qty || Number(qty) <= 0) {
      toast.error('Masukkan jumlah restok');
      return;
    }
    try {
      await api.restockFood(item.id, Number(qty));
      toast.success(`Stok bertambah ${qty}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal restok');
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-cyan-500/40 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">Restok Item</DialogTitle>
        </DialogHeader>
        {item && (
          <>
            <div className="rounded-lg bg-[#0E1424] border border-slate-800 p-3 mb-2">
              <p className="font-semibold text-slate-100">{item.name}</p>
              <p className="text-xs text-slate-400">Stok Saat Ini: <span className="text-emerald-400 font-mono-tactical">{item.stock}</span></p>
            </div>
            <div>
              <Label className="text-slate-300">Jumlah Restok</Label>
              <Input data-testid="input-restock-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="10" className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical" />
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Batal</Button>
          <Button data-testid="btn-confirm-restock" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-[#090D16] font-semibold gap-2">
            <PackagePlus className="w-4 h-4" /> Tambah Stok
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
